import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  Loader2, RefreshCw, Trash2, Plus, Pencil, Zap, CalendarClock, Clock, X,
  ToggleLeft, ToggleRight, History, CheckCircle2, AlertCircle, HeartPulse,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DOM_BASE = `${BACKEND_URL}/api/super-admin/sandbox/domiciliacion`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-VE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(iso); }
}
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return String(iso); }
}

const HEALTH_STYLES = {
  good: { dot: 'bg-green-500', badge: 'bg-green-100 text-green-700 border-green-200' },
  warning: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  bad: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 border-red-200' },
};

function HealthBadge({ stats, mandateId }) {
  if (!stats || !stats.total) {
    return <span className="text-[10px] text-slate-400" data-testid={`mandate-health-empty-${mandateId}`}>Sin cobros</span>;
  }
  const style = HEALTH_STYLES[stats.health] || HEALTH_STYLES.good;
  const tooltip = `Últimos ${stats.total} cobros: ${(stats.last_codes || []).join(', ')}`;
  return (
    <span
      title={tooltip}
      data-testid={`mandate-health-${mandateId}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-medium ${style.badge}`}
    >
      <span className={`w-2 h-2 rounded-full ${style.dot}`} />
      <span className="font-mono">
        {stats.ok} OK
        {stats.failed > 0 && <> · <span className="text-red-600">{stats.failed} fallido{stats.failed > 1 ? 's' : ''}</span></>}
        {stats.pending > 0 && <> · <span className="text-amber-600">{stats.pending} pend.</span></>}
      </span>
    </span>
  );
}

const EMPTY = {
  alias: '', id_cobrador: '', cuenta_cobrador: '', contrato: '', fecha_contrato: '',
  identificacion_d: '', cuenta_deudor: '', nombre_d: '', monto: '', concepto: '', charge_day: 1,
};

function MandateForm({ initial, cobradorDefault, onSave, onCancel, busy }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    id_cobrador: cobradorDefault?.rif || '',
    cuenta_cobrador: cobradorDefault?.cuenta || '',
    fecha_contrato: new Date().toISOString().slice(0, 10),
    ...(initial || {}),
  }));
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form
      data-testid="dom-mandate-form"
      onSubmit={(e) => { e.preventDefault(); onSave({ ...form, monto: parseFloat(form.monto), charge_day: parseInt(form.charge_day, 10) }); }}
      className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50 border border-slate-200 rounded-lg p-4"
    >
      <div className="md:col-span-2">
        <label className="text-[11px] text-slate-500">Alias (etiqueta interna)</label>
        <input value={form.alias} onChange={upd('alias')} className="w-full px-3 py-1.5 border rounded text-sm" placeholder="Suscripción mensual Juan" data-testid="mandate-alias" />
      </div>
      <div>
        <label className="text-[11px] text-slate-500">RIF cobrador</label>
        <input value={form.id_cobrador} onChange={upd('id_cobrador')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" data-testid="mandate-id-cobrador" />
      </div>
      <div>
        <label className="text-[11px] text-slate-500">Cuenta cobrador (20 díg.)</label>
        <input value={form.cuenta_cobrador} onChange={upd('cuenta_cobrador')} maxLength={20} className="w-full px-3 py-1.5 border rounded text-sm font-mono" data-testid="mandate-cuenta-cobrador" />
      </div>
      <div>
        <label className="text-[11px] text-slate-500">Contrato</label>
        <input value={form.contrato} onChange={upd('contrato')} maxLength={15} required className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="202411151239" data-testid="mandate-contrato" />
      </div>
      <div>
        <label className="text-[11px] text-slate-500">Fecha contrato</label>
        <input type="date" value={form.fecha_contrato} onChange={upd('fecha_contrato')} required className="w-full px-3 py-1.5 border rounded text-sm font-mono" data-testid="mandate-fecha-contrato" />
      </div>
      <div>
        <label className="text-[11px] text-slate-500">RIF/Cédula deudor</label>
        <input value={form.identificacion_d} onChange={upd('identificacion_d')} required className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="E00000722125" data-testid="mandate-identificacion-d" />
      </div>
      <div>
        <label className="text-[11px] text-slate-500">Cuenta deudor (20 díg.)</label>
        <input value={form.cuenta_deudor} onChange={upd('cuenta_deudor')} maxLength={20} required className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="01380007350070010802" data-testid="mandate-cuenta-deudor" />
      </div>
      <div>
        <label className="text-[11px] text-slate-500">Nombre deudor</label>
        <input value={form.nombre_d} onChange={upd('nombre_d')} maxLength={80} required className="w-full px-3 py-1.5 border rounded text-sm" placeholder="DA CONCEICAO LUISA" data-testid="mandate-nombre-d" />
      </div>
      <div>
        <label className="text-[11px] text-slate-500">Monto (VES)</label>
        <input type="number" step="0.01" min="0.01" value={form.monto} onChange={upd('monto')} required className="w-full px-3 py-1.5 border rounded text-sm font-mono font-bold" placeholder="150.00" data-testid="mandate-monto" />
      </div>
      <div>
        <label className="text-[11px] text-slate-500">Día de cobro (1-28)</label>
        <input type="number" min="1" max="28" value={form.charge_day} onChange={upd('charge_day')} required className="w-full px-3 py-1.5 border rounded text-sm font-mono" data-testid="mandate-charge-day" />
      </div>
      <div className="md:col-span-2">
        <label className="text-[11px] text-slate-500">Concepto</label>
        <input value={form.concepto} onChange={upd('concepto')} maxLength={60} required className="w-full px-3 py-1.5 border rounded text-sm" placeholder="Mensualidad servicio" data-testid="mandate-concepto" />
      </div>
      <div className="md:col-span-2 flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-100" data-testid="mandate-cancel-btn">Cancelar</button>
        <button type="submit" disabled={busy} className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2" data-testid="mandate-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Guardar mandato
        </button>
      </div>
    </form>
  );
}

export default function DomiciliacionManager({ setToast, cobradorDefault }) {
  const [mandates, setMandates] = useState([]);
  const [health, setHealth] = useState({});
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chargingId, setChargingId] = useState(null);

  const [polling, setPolling] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [pollBusy, setPollBusy] = useState(false);

  const [historyFor, setHistoryFor] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadMandates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${DOM_BASE}/mandates`, authHeaders());
      setMandates(res.data || []);
    } catch { setMandates([]); } finally { setLoading(false); }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${DOM_BASE}/mandates-health?window=6`, authHeaders());
      setHealth(res.data || {});
    } catch { /* no-op */ }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        axios.get(`${DOM_BASE}/polling-status`, authHeaders()),
        axios.get(`${DOM_BASE}/scheduler-status`, authHeaders()),
      ]);
      setPolling(p.data);
      setScheduler(s.data);
    } catch { /* no-op */ }
  }, []);

  useEffect(() => { loadMandates(); loadStatus(); loadHealth(); }, [loadMandates, loadStatus, loadHealth]);

  const saveMandate = async (payload) => {
    setBusy(true);
    try {
      if (editing) {
        await axios.put(`${DOM_BASE}/mandates/${editing.mandate_id}`, payload, authHeaders());
        setToast?.({ type: 'success', message: 'Mandato actualizado' });
      } else {
        await axios.post(`${DOM_BASE}/mandates`, payload, authHeaders());
        setToast?.({ type: 'success', message: 'Mandato creado' });
      }
      setShowForm(false); setEditing(null);
      await loadMandates(); await loadStatus();
    } catch (err) {
      const d = err.response?.data?.detail;
      setToast?.({ type: 'error', message: typeof d === 'string' ? d : (d?.error || err.message) });
    } finally { setBusy(false); }
  };

  const toggleActive = async (m) => {
    try {
      await axios.put(`${DOM_BASE}/mandates/${m.mandate_id}`, { active: !m.active }, authHeaders());
      await loadMandates();
    } catch (err) { setToast?.({ type: 'error', message: err.message }); }
  };

  const removeMandate = async (m) => {
    if (!window.confirm(`¿Eliminar el mandato de ${m.nombre_d}?`)) return;
    try {
      await axios.delete(`${DOM_BASE}/mandates/${m.mandate_id}`, authHeaders());
      setToast?.({ type: 'success', message: 'Mandato eliminado' });
      await loadMandates(); await loadStatus();
    } catch (err) { setToast?.({ type: 'error', message: err.message }); }
  };

  const chargeNow = async (m) => {
    setChargingId(m.mandate_id);
    try {
      const res = await axios.post(`${DOM_BASE}/mandates/${m.mandate_id}/charge-now`, {}, authHeaders());
      const s = res.data?.summary || {};
      setToast?.({ type: res.data.ok ? 'success' : (res.data.pendiente ? 'warning' : 'warning'), message: `[${s.codigo}] ${s.mensaje}` });
      await loadMandates(); await loadStatus(); await loadHealth();
      if (historyFor === m.mandate_id) await loadHistory(m.mandate_id);
    } catch (err) {
      const d = err.response?.data?.detail;
      setToast?.({ type: 'error', message: typeof d === 'string' ? d : (d?.error || err.message) });
    } finally { setChargingId(null); }
  };

  const pollNow = async () => {
    setPollBusy(true);
    try {
      const res = await axios.post(`${DOM_BASE}/poll-now`, {}, authHeaders());
      setToast?.({ type: 'success', message: `Consulta ejecutada: ${res.data.checked} cobro(s) revisado(s)` });
      await loadStatus();
      if (historyFor) await loadHistory(historyFor);
    } catch (err) { setToast?.({ type: 'error', message: err.message }); } finally { setPollBusy(false); }
  };

  const loadHistory = useCallback(async (mandateId) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${DOM_BASE}/mandates/${mandateId}/history`, authHeaders());
      setHistoryRows(res.data?.history || []);
    } catch { setHistoryRows([]); } finally { setHistoryLoading(false); }
  }, [historyFor]);

  const toggleHistory = async (m) => {
    if (historyFor === m.mandate_id) { setHistoryFor(null); setHistoryRows([]); return; }
    setHistoryFor(m.mandate_id);
    await loadHistory(m.mandate_id);
  };

  return (
    <div data-testid="dom-manager" className="mt-8 space-y-6">
      {/* Seguimiento de liquidación (PE) */}
      <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-4" data-testid="dom-polling-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600" />
            <h4 className="text-sm font-semibold text-slate-700">Seguimiento de liquidación (cobros en <code>PE</code>)</h4>
          </div>
          <button onClick={pollNow} disabled={pollBusy} className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="dom-poll-now-btn">
            {pollBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Consultar ahora
          </button>
        </div>
        <p className="text-[11px] text-amber-800 mb-3">
          El job consulta cada 5 min los cobros pendientes por liquidación BCV y los pasa a <code>0000</code> (liquidado) automáticamente.
          {polling && <> Job: <strong>{polling.running ? 'activo' : 'detenido'}</strong> · resueltos OK {polling.resolved_ok} · pendientes {polling.pending_count}.</>}
        </p>
        {polling?.pending?.length > 0 ? (
          <div className="space-y-1.5" data-testid="dom-pending-list">
            {polling.pending.map((p) => (
              <div key={p.test_id} className="flex items-center justify-between text-xs bg-white border border-amber-200 rounded px-3 py-1.5">
                <span className="font-mono text-slate-600">{p.nombre_d || p.identificacion_d} · {Number(p.monto).toLocaleString('es-VE')} VES</span>
                <span className="text-amber-700">TX {p.transaccionId?.slice(0, 12)}… · intentos {p.poll_count} · {fmtDateTime(p.last_poll_at)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400" data-testid="dom-pending-empty">No hay cobros pendientes por liquidación.</p>
        )}
      </div>

      {/* Mandatos recurrentes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-purple-600" />
            <h4 className="text-sm font-semibold text-slate-700">Mandatos de domiciliación (cobro mensual automático)</h4>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { loadMandates(); loadStatus(); loadHealth(); }} className="px-2.5 py-1.5 text-xs border rounded-lg hover:bg-slate-100 inline-flex items-center gap-1.5" data-testid="dom-mandates-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Actualizar
            </button>
            <button onClick={() => { setEditing(null); setShowForm((v) => !v); }} className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 inline-flex items-center gap-1.5" data-testid="dom-mandate-new-btn">
              {showForm && !editing ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {showForm && !editing ? 'Cerrar' : 'Nuevo mandato'}
            </button>
          </div>
        </div>

        <p className="text-[11px] text-slate-500 mb-3">
          El comercio registra el mandato (respaldo jurídico del cargo autorizado por el deudor). El scheduler cobra cada mes en el día indicado, sin reingresar datos.
        </p>

        {showForm && (
          <div className="mb-4">
            <MandateForm
              initial={editing ? {
                alias: editing.alias || '', id_cobrador: editing.id_cobrador, cuenta_cobrador: editing.cuenta_cobrador,
                contrato: editing.contrato, fecha_contrato: (editing.fecha_contrato || '').slice(0, 10),
                identificacion_d: editing.identificacion_d, cuenta_deudor: editing.cuenta_deudor,
                nombre_d: editing.nombre_d, monto: editing.monto, concepto: editing.concepto, charge_day: editing.charge_day,
              } : null}
              cobradorDefault={cobradorDefault}
              busy={busy}
              onSave={saveMandate}
              onCancel={() => { setShowForm(false); setEditing(null); }}
            />
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : mandates.length === 0 ? (
          <p className="text-xs text-slate-400 py-4 text-center" data-testid="dom-mandates-empty">Aún no hay mandatos registrados.</p>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs" data-testid="dom-mandates-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Deudor</th>
                  <th className="text-left px-3 py-2 font-medium">Monto</th>
                  <th className="text-left px-3 py-2 font-medium">Día</th>
                  <th className="text-left px-3 py-2 font-medium">Próximo cobro</th>
                  <th className="text-left px-3 py-2 font-medium">Últ. cobro</th>
                  <th className="text-left px-3 py-2 font-medium"><span className="inline-flex items-center gap-1"><HeartPulse className="w-3.5 h-3.5" />Salud de pago</span></th>
                  <th className="text-left px-3 py-2 font-medium">Estado</th>
                  <th className="text-right px-3 py-2 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {mandates.map((m) => (
                  <Fragment key={m.mandate_id}>
                  <tr className="border-t hover:bg-slate-50" data-testid={`mandate-row-${m.mandate_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-700">{m.nombre_d}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{m.alias || m.identificacion_d}</div>
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold">{Number(m.monto).toLocaleString('es-VE')} {m.moneda}</td>
                    <td className="px-3 py-2 font-mono">{m.charge_day}</td>
                    <td className="px-3 py-2">{fmtDate(m.next_charge_at)}</td>
                    <td className="px-3 py-2">
                      {m.last_charge_at ? (
                        <span title={m.last_charge_code}>{fmtDateTime(m.last_charge_at)} <span className="font-mono text-slate-400">[{m.last_charge_code}]</span></span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <HealthBadge stats={health[m.mandate_id]} mandateId={m.mandate_id} />
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => toggleActive(m)} className="inline-flex items-center gap-1" data-testid={`mandate-toggle-${m.mandate_id}`}>
                        {m.active
                          ? <><ToggleRight className="w-4 h-4 text-green-600" /><span className="text-green-700">Activo</span></>
                          : <><ToggleLeft className="w-4 h-4 text-slate-400" /><span className="text-slate-400">Pausado</span></>}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggleHistory(m)} title="Historial de cobros" className={`p-1.5 rounded hover:bg-blue-100 ${historyFor === m.mandate_id ? 'bg-blue-100 text-blue-700' : 'text-blue-600'}`} data-testid={`mandate-history-${m.mandate_id}`}>
                          <History className="w-4 h-4" />
                        </button>
                        <button onClick={() => chargeNow(m)} disabled={chargingId === m.mandate_id} title="Cobrar ahora" className="p-1.5 rounded hover:bg-purple-100 text-purple-600 disabled:opacity-50" data-testid={`mandate-charge-${m.mandate_id}`}>
                          {chargingId === m.mandate_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        </button>
                        <button onClick={() => { setEditing(m); setShowForm(true); }} title="Editar" className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`mandate-edit-${m.mandate_id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => removeMandate(m)} title="Eliminar" className="p-1.5 rounded hover:bg-red-100 text-red-500" data-testid={`mandate-delete-${m.mandate_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {historyFor === m.mandate_id && (
                    <tr className="bg-slate-50/70" data-testid={`mandate-history-panel-${m.mandate_id}`}>
                      <td colSpan={8} className="px-4 py-3">
                        <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                          <History className="w-3.5 h-3.5" /> Historial de cobros de {m.nombre_d}
                        </div>
                        {historyLoading ? (
                          <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
                        ) : historyRows.length === 0 ? (
                          <p className="text-xs text-slate-400 py-2" data-testid={`mandate-history-empty-${m.mandate_id}`}>Este mandato aún no tiene cobros registrados.</p>
                        ) : (
                          <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3" data-testid={`mandate-history-list-${m.mandate_id}`}>
                            {historyRows.map((h) => {
                              const ok = h.success || h.codigo === '0000';
                              const pending = String(h.codigo).toUpperCase() === 'PE';
                              return (
                                <li key={h.test_id} className="ml-4">
                                  <span className={`absolute -left-[7px] w-3 h-3 rounded-full border-2 border-white ${ok ? 'bg-green-500' : pending ? 'bg-amber-500' : 'bg-red-500'}`} />
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                                    <span className="text-slate-500 font-mono">{fmtDateTime(h.created_at)}</span>
                                    <span className="font-semibold font-mono">{Number(h.monto).toLocaleString('es-VE')} VES</span>
                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] ${ok ? 'bg-green-100 text-green-700' : pending ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                      {ok ? <CheckCircle2 className="w-3 h-3" /> : pending ? <Clock className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                                      {h.codigo || '—'}
                                    </span>
                                    <span className="text-slate-500">{h.descripcion || ''}</span>
                                    {h.referencia && <span className="text-slate-400 font-mono text-[10px]">ref {h.referencia}</span>}
                                    <span className="text-slate-300 text-[10px]">· {h.origin === 'scheduler' ? 'automático' : 'manual'}</span>
                                  </div>
                                </li>
                              );
                            })}
                          </ol>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {scheduler && (
          <p className="text-[10px] text-slate-400 mt-2" data-testid="dom-scheduler-info">
            Scheduler mensual: <strong>{scheduler.running ? 'activo' : 'detenido'}</strong> · mandatos activos {scheduler.active_mandates} · cobros ejecutados {scheduler.mandates_charged}.
          </p>
        )}
      </div>
    </div>
  );
}
